<template>

    <div>
        <el-backtop :right="40" :bottom="40" />
        <div v-loading="documentStore.loading"
             class="post">

            <TranslationConfig v-if="translationConfig"
                               v-model="translationConfig"
                               @configChanged="saveConfig"
                               :documentType="documentType"
                               :pageNamePrefix="$t('documentPage.configurationPrefix')">


                <el-divider />
                <DocumentActions v-model="translationConfig" />
                <el-divider />
                <ProofreadingActions v-if="documentData"
                                     :document="documentData"
                                     @reload="loadConfigurations" />
            </TranslationConfig>

            <DocumentStatistics v-if="documentStatisticsExist"
                                :documentName="documentName"
                                :documentInformation="documentInformation"
                                :documentType="documentType"
                                :abbreviationSnapshot="abbreviationSnapshot"
                                :createdAt="documentData?.createdAt"
                                :updatedAt="documentData?.updatedAt"
                                :segmentCount="segmentCounts?.totalSegments"
                                :mergedSegmentCount="segmentCounts?.mergedSegments" />

            <div class="sticky-search-container">
                <SearchControl :translationConfig="translationConfig" />
            </div>

            <div class="segmnets-editor-container">

                <SegmentEditor :key="pageKey + 'N'"
                               :documentConfiguration="documentData"
                               @reload="loadConfigurations" />

                <div class="paging">
                    <el-pagination layout="prev, pager, next"
                                   :total="pagingStore.rowCount"
                                   :current-page="pagingStore.currentPageNumber"
                                   :default-page-size="pagingStore.pageSize"
                                   @current-change="goToPage" />
                </div>
            </div>

        </div>

        <HotkeysHandler />
    </div>

</template>

<script lang="ts">
    import { defineComponent } from 'vue';

    import { DocumentType } from '@/models/DocumentType'
    import { Document } from '@/models/Document'
    import { TranslationConfiguration } from '@/models/TranslationConfiguration';
    import { AbbreviationSnapshot } from '@/models/AbbreviationSettings';
    import { SegmentCounts } from '@/models/SegmentCounts';

    import { documentStore } from '@/stores/documentStore'
    import { pagingStore } from '@/stores/pagingStore'

    import { documentService } from './documentService'

    import TranslationConfig from '@/features/TranslationConfig/TranslationConfig.vue'
    import DocumentStatistics from './DocumentStatistics.vue'
    import SearchControl from './Search/SearchControl.vue'
    import DocumentActions from './Actions/DocumentActions.vue'
    import SegmentEditor from './SegmentEditor.vue'
    import HotkeysHandler from './HotkeysHandler.vue'
    import ProofreadingActions from '@/pages/Proofreading/ProofreadingActions.vue'

    export default defineComponent({
        components: {
            TranslationConfig,
            DocumentActions,
            SegmentEditor,
            HotkeysHandler,
            DocumentStatistics,
            SearchControl,
            ProofreadingActions,
        },
        data() {
            return {
                loading: false,
                pageKey: 0,
                documentService: new documentService(),
                documentData: null as Document | null,
                documentName: '',
                translationConfig: null as TranslationConfiguration | null,
                documentInformation: null,
                segmentCounts: null as SegmentCounts | null,
                reload: false,
                pagingStore: pagingStore(),
                documentStore: documentStore(),
            };
        },
        computed: {
            documentId(): number {
                return Number(this.$route.params.id);
            },
            documentType(): DocumentType {
                return this.documentData != null
                    ? this.documentData.documentType
                    : DocumentType.Docx;
            },
            documentStatisticsExist(): boolean {
                return this.documentInformation != null;
            },
            abbreviationSnapshot(): AbbreviationSnapshot | null {
                return this.translationConfig?.abbreviationSnapshot ?? null;
            },
        },
        watch: {
            documentId: {
                immediate: true,
                handler(newVal) {
                    if (isNaN(newVal)) {
                        return;
                    }

                    this.documentStore.setDocumntId(newVal);
                    this.pagingStore.resetState();
                    this.loadConfigurations();
                }
            },
            async 'documentStore.documentUpdate'() {
                this.pageKey++;
                await this.loadConfigurations();
            },
        },

        methods: {
            async loadConfigurations() {
                this.documentStore.startLoading();

                this.documentData = await this.documentService.loadConfigurations(this.documentId)

                if (this.documentData != null) {
                    this.translationConfig = this.documentData.translationConfig;
                    this.documentInformation = this.documentData.documentInformation;
                    this.documentName = this.documentData.name;
                    this.documentStore.setDocumentType(this.documentData.documentType);

                    this.segmentCounts = await this.documentService.loadSegmentCounts(this.documentId);
                }

                this.documentStore.stopLoading();
            },
            async saveConfig() {
                await this.documentService.saveConfig(this.documentId, this.translationConfig);
            },
            goToPage(pageNumebr: number) {
                this.pagingStore.goToPage(pageNumebr);
            },
        },

    });
</script>

<style lang="scss" scoped>
    .sticky-search-container {
        position: sticky;
        top: 0;
        z-index: 100;
        box-shadow: var(--el-box-shadow-light);
    }

    .segmnets-editor-container {
        margin-top: 40px;
        background-color: #ffffff;
        color: var(--text-color);
        transition: 0.3s;

        .paging {
            display: flex;
            justify-content: space-around;
            padding: 20px 0;
        }
    }
</style>